/**
 * Navbar
 *
 * Responsive top navigation bar.
 * - Logo + brand name
 * - "Give" persistent CTA
 * - Stories toggle button
 * - Auth-gated menu items (Submit Catalyst, Catalysts, Dashboard, Admin)
 * - User avatar + dropdown (Profile, Settings, Sign out)
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { useMapStore } from '@/stores/mapStore';
import { useUIStore } from '@/stores/uiStore';
import { useCartStore } from '@/stores/cartStore';
import { hasRole, ROLES } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useOrganisations } from '@/hooks/useOrganisations';

export default function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const openGiveModal = useMapStore((s) => s.openGiveModal);
  const toggleSidebar = useMapStore((s) => s.toggleSidebar);
  const openContributorPortal = useUIStore((s) => s.openContributorPortal);
  const openCart = useCartStore((s) => s.openCart);
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const role = user?.role || ROLES.PUBLIC;
  const isContributor = hasRole(role, ROLES.CONTRIBUTOR);
  const isSiteOwner = hasRole(role, ROLES.SITE_OWNER);
  const isAdmin = hasRole(role, ROLES.ADMIN);

  const canManageAllGroups = isSiteOwner || isAdmin;
  const { data: orgs } = useOrganisations(isAuthenticated && !canManageAllGroups);
  const groupId = orgs?.[0]?.id;

  const DRUPAL_DIRECT = import.meta.env.VITE_DRUPAL_DIRECT_URL || '';

  return (
    <nav className="relative z-30 flex h-14 items-center justify-between border-b border-neutral-200 bg-parchment/95 px-4 backdrop-blur-sm">
      {/* ── Logo ─────────────────────────────────────────────────────────────── */}
      <Link to="/" className="flex items-center gap-2.5">
        <span className="relative flex items-center gap-3">
          {/* Pulse mark */}
          <span className="relative flex h-3 w-3 items-center justify-center">
            <span className="animate-impact-pulse absolute inline-flex h-full w-full rounded-full bg-[#c0392b] opacity-35" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-[#c0392b]" />
          </span>
          <span className="text-[22px] font-normal tracking-[-0.025em] text-ink leading-none hidden sm:block">impact</span>
        </span>
      </Link>

      {/* ── Center nav links ──────────────────────────────────────────────────── */}
      <div className="hidden md:flex items-center gap-1">
        <NavLink to="/" current={location.pathname === '/'}>Map</NavLink>

        {isContributor && (
          <button
            onClick={openContributorPortal}
            className="rounded-lg px-3 py-1.5 text-sm text-neutral-500 transition hover:bg-paper hover:text-ink"
          >
            Submit Catalyst
          </button>
        )}

        {isContributor && (
          <NavLink to="/my-catalysts" current={location.pathname === '/my-catalysts'}>Catalysts</NavLink>
        )}

        {isSiteOwner && (
          <NavLink to="/dashboard" current={location.pathname === '/dashboard'}>Dashboard</NavLink>
        )}

        {(isSiteOwner || orgs?.length > 0) && (
          <NavLink to="/timelines" current={location.pathname.startsWith('/timelines')}>Timelines</NavLink>
        )}

        {isAdmin && (
          <NavLink to="/admin" current={location.pathname.startsWith('/admin')}>Admin</NavLink>
        )}
      </div>

      {/* ── Right controls ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2">
        {/* Stories toggle */}
        <button
          onClick={toggleSidebar}
          aria-label="Toggle stories"
          className="rounded-lg p-2 text-neutral-500 transition hover:bg-paper hover:text-ink"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
          </svg>
        </button>

        {/* Subscription plans */}
        <button
          onClick={openCart}
          aria-label="View subscription plans"
          className="rounded-lg p-2 text-neutral-500 transition hover:bg-paper hover:text-ink"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        </button>

        {/* Auth */}
        {isAuthenticated ? (
          <div className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-neutral-200 bg-paper text-sm font-semibold text-ink transition hover:border-[#003cc5]"
            >
              {user?.avatarUrl ? (
                <img src={user.avatarUrl} alt={user.name} className="h-full w-full object-cover" />
              ) : (
                user?.name?.[0]?.toUpperCase() || '?'
              )}
            </button>

            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 top-10 z-20 w-48 rounded-xl border border-neutral-200 bg-paper py-1 shadow-xl animate-fade-in">
                  <div className="border-b border-neutral-200 px-4 py-2.5">
                    <p className="text-sm font-medium text-ink">{user?.name}</p>
                    <p className="text-xs text-neutral-500 capitalize">{role.replace('_', ' ')}</p>
                  </div>
                  {user?.id && (
                    <a
                      href={`${import.meta.env.VITE_DRUPAL_DIRECT_URL || ''}/user/${user.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-ink hover:bg-parchment"
                    >
                      Profile ↗
                    </a>
                  )}
                  {isAuthenticated && user?.id && (
                    <a
                      href={`${import.meta.env.VITE_DRUPAL_DIRECT_URL || ''}/user/${user.id}/orders`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-ink hover:bg-parchment"
                    >
                      Billing ↗
                    </a>
                  )}
                  {isAuthenticated && (canManageAllGroups || groupId) && (
                    <a
                      href={
                        canManageAllGroups
                          ? `${DRUPAL_DIRECT}/admin/group`
                          : `${DRUPAL_DIRECT}/group/${groupId}/members`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-ink hover:bg-parchment"
                    >
                      Manage Organization ↗
                    </a>
                  )}
                  <button
                    onClick={() => { logout(); setMenuOpen(false); navigate('/'); }}
                    className="block w-full px-4 py-2 text-left text-sm text-[#003cc5] hover:bg-parchment"
                  >
                    Sign Out
                  </button>
                </div>
              </>
            )}
          </div>
        ) : (
          <Link
            to="/login"
            className="rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-500 transition hover:border-[#003cc5] hover:text-ink"
          >
            Sign In
          </Link>
        )}
      </div>
    </nav>
  );
}

function NavLink({ to, current, children }) {
  return (
    <Link
      to={to}
      className={cn(
        'rounded-lg px-3 py-1.5 text-sm transition',
        current
          ? 'bg-paper text-ink font-medium'
          : 'text-neutral-500 hover:bg-paper hover:text-ink'
      )}
    >
      {children}
    </Link>
  );
}
